import {
    paramAppend,
    paramContextNode,
    paramDefault,
    paramDsName,
    paramEval,
    paramRelDest,
    paramXPath,
    paramXPathNode,
    paramXPathSrc,
} from '../constants/parameterNames';
import {
    typeBoolean,
    typeDataSource,
    typeXPath,
} from '../constants/parameterTypes';
import { success } from '../helpers/commandResult';
import {
    getBoolParamValue,
    getParamValue,
} from '../helpers/parameter.accessors';
import { getDataSource } from '../helpers/qRules.dataSource.helpers';
import { getValidNode, getValidNodeSet } from '../helpers/qRules.node.helpers';

const requiredParameters = [];

const optionalParameters = [
    {
        name: paramDsName,
        description: 'The name of the data source from which to get a value.',
        type: typeDataSource,
    },
    {
        name: paramXPath,
        description: 'XPath from which to get a value.',
        type: typeXPath,
        dsParentName: paramDsName,
    },
    {
        name: paramXPathNode,
        description:
            `Relative XPath to node containing an XPath to evaluate - can only be used if /${paramEval} is true and /${paramRelDest} is specified.\n\n` +
            `Takes precedence over /${paramXPath}.`,
        type: typeXPath,
        dsParentName: paramDsName,
        relativeTo: paramXPath,
    },
    {
        name: paramEval,
        description:
            '(boolean, defaults to false) Evaluate the XPath as an expression rather than retrieving a node value.',
        type: typeBoolean,
    },
    {
        name: paramXPathSrc,
        description:
            `When using /${paramEval}=true, the path to the nodes(s) relative to which XPath should be evaluated, UNLESS /${paramContextNode} is specified, in which case XPaths will be evaluated relative to that node.\n\n` +
            `If /${paramXPathNode} is specified, the path in /${paramXPathNode} will be evaluated relative to these nodes.\n\n` +
            `Required if /${paramEval} is true and /${paramRelDest} is specified.`,
        type: typeXPath,
        dsParentName: paramDsName,
    },
    {
        name: paramContextNode,
        description: `Context node for evaluation - can only be used if /${paramEval} is true and /${paramRelDest} is specified.`,
        type: typeXPath,
        dsParentName: paramDsName,
    },
    {
        name: paramRelDest,
        description: `XPath relative to the value of the /${paramXPath} or /${paramXPathSrc} parameter, depending on whether /${paramEval} is true. When specified, qRules will place results of the XPath evaluation(s) in the field indicated by /${paramRelDest}`,
        type: typeXPath,
        dsParentName: paramDsName,
        relativeTo: paramXPath,
    },
    {
        name: paramAppend,
        description: `(boolean, defaults to false). If true, the result of the string replacement is appended to the current value of the field indicated by the /${paramRelDest} parameter rather than replacing it.`,
        type: typeBoolean,
    },
    {
        name: paramDefault,
        description:
            'Default value to return to the results node if no value or a blank value is found.',
    },
];

const getValueErrorBlankXPath =
    'No /xpath or /xpathsrc provided for field selection.';
const getValueSingleParamError = '/{0} cannot be used without including /{1}.';
const getValueErrorBlankXPathSrc =
    'If /eval is true and a /reldest is provided, /xpathsrc is required.';
const getValueErrorFailedToSelectNode = 'Failed to select field.';
const errorFailedToSelectSource = 'Failed to select the source node.';
const errorFailedToSelectDestination = 'Failed to select the destination node.';
const description =
    'Returns the value for a given XPath as a string. If the node is not present or blank, a default value can be returned.';

function validateGetValueParams(
    xPath,
    xPathNode,
    xPathSrc,
    evalValue,
    contextNodeValue,
    relDest,
    append,
) {
    if (!xPath && !xPathSrc) {
        // [2023-04-20] - Removing use of getUsageTextForCommand() since it overcomplicates dependencies
        //                and isn't used in other commands.
        throw new Error(getValueErrorBlankXPath);
    }

    if (relDest) {
        if (!xPathSrc && evalValue) {
            throw new Error(getValueErrorBlankXPathSrc);
        }
    } else {
        if (xPathNode) {
            throw new Error(
                String.format(
                    getValueSingleParamError,
                    paramXPathNode,
                    paramRelDest,
                ),
            );
        }
        if (contextNodeValue) {
            throw new Error(
                String.format(
                    getValueSingleParamError,
                    paramContextNode,
                    paramRelDest,
                ),
            );
        }
        if (append) {
            throw new Error(
                String.format(
                    getValueSingleParamError,
                    paramAppend,
                    paramRelDest,
                ),
            );
        }
    }
}

const getNodeValue = (node) => (!node ? '' : node.value());

function getEvaluatedValue(xPath, node) {
    const expression = `string(${xPath})`;

    try {
        return node.evaluateString(expression);
    } catch (e) {
        console.error('GetValue XPath evaluation error: ', e);
        throw new Error(
            `An error occurred evaluating the xpath '${xPath}': ${e.message}`,
        );
    }
}

function getSingleValue(ds, xPath, xPathSrc, evalValue) {
    if (evalValue) {
        const node = xPathSrc
            ? getValidNode(ds, xPathSrc, getValueErrorFailedToSelectNode)
            : null;

        return getEvaluatedValue(xPath, node || ds);
    }

    const node = getValidNode(ds, xPath, getValueErrorFailedToSelectNode);

    return getNodeValue(node);
}

const getValueForMultiSet = (
    node,
    evalValue,
    xPathNode,
    xPath,
    contextNode,
) => {
    if (evalValue) {
        //if there is a context node, use that, otherwise use the current node

        //if there is an xpathnode, it should be the relative path to the node that holds the xpath to evaluate.
        //if not, use xpath (i.e., like the single version, eval == true indicates that instead of signifying a node,
        //the xpath param will have the xpath to evaluate

        const evalXPath = xPathNode
            ? getValidNode(
                  node,
                  xPathNode,
                  getValueErrorFailedToSelectNode,
              ).value()
            : xPath;

        return getEvaluatedValue(evalXPath, contextNode || node);
    }

    return getNodeValue(node);
};

const getMultiValue = async (
    ds,
    xPathContextNode,
    xPathSrc,
    xPath,
    xPathNode,
    defaultValue,
    evalValue,
    relDest,
    append,
) => {
    const sourceNodes = getValidNodeSet(
        ds,
        evalValue ? xPathSrc : xPath,
        errorFailedToSelectSource,
    );
    //get context node if contextnode param specified
    const contextNode = xPathContextNode
        ? getValidNode(ds, xPathContextNode, getValueErrorFailedToSelectNode)
        : null;
    let getCount = 0;

    for (const node of sourceNodes) {
        const valueString =
            getValueForMultiSet(
                node,
                evalValue,
                xPathNode,
                xPath,
                contextNode,
            ) || defaultValue;

        const dest = getValidNode(
            node,
            relDest,
            errorFailedToSelectDestination,
        );

        const fullValue = append ? dest.value() + valueString : valueString;

        await dest.setValueAsync(fullValue);

        getCount += 1;
    }

    return getCount;
};

async function executeAsync({ dataSources, parameters }) {
    const dsName = getParamValue(parameters, paramDsName);
    const xPath = getParamValue(parameters, paramXPath);
    const xPathNode = getParamValue(parameters, paramXPathNode);
    const xPathSrc = getParamValue(parameters, paramXPathSrc);
    const defaultValue = getParamValue(parameters, paramDefault, '');
    const evalValue = getBoolParamValue(parameters, paramEval);
    const contextNodeValue = getParamValue(parameters, paramContextNode);
    const relDest = getParamValue(parameters, paramRelDest);
    const append = getBoolParamValue(parameters, paramAppend);
    const isSingleValue = !relDest;

    const ds = getDataSource(dataSources, dsName);

    validateGetValueParams(
        xPath,
        xPathNode,
        xPathSrc,
        evalValue,
        contextNodeValue,
        relDest,
        append,
    );

    if (isSingleValue) {
        const result = getSingleValue(ds, xPath, xPathSrc, evalValue);

        return success(result || defaultValue);
    }

    // multi-value
    return success(
        await getMultiValue(
            ds,
            contextNodeValue,
            xPathSrc,
            xPath,
            xPathNode,
            defaultValue,
            evalValue,
            relDest,
            append,
        ),
    );
}

const GetValue = {
    description,
    optionalParameters,
    requiredParameters,
    executeAsync,
};

export default GetValue;
